<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Stany materiałów</span>
        <h3 class="page-title">Lista stanów materiałów - historia zużycia</h3>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="materials-stock-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <v-select
          class="w-50"
          :options="materialsList"
          v-model="filters.material_id"
          placeholder="Wybierz materiał"
          :searchable="true"
          :clearable="true"
          :multiple="false"
          :reduce="option => option.value"
          label="text">
        </v-select>

        <v-select
          class="w-50 ml-2"
          :options="materialColors"
          v-model="filters.color_id"
          placeholder="Wybierz kolor"
          :searchable="true"
          :clearable="true"
          :multiple="false"
          :reduce="option => option.value"
          label="text">
        </v-select>

        <v-select
          class="w-50 ml-2"
          :options="sizesList"
          v-model="filters.size_id"
          placeholder="Wybierz rozmiar"
          :searchable="true"
          :clearable="true"
          :multiple="false"
          :reduce="option => option.value"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Nazwa materiału i koloru</th>
            <th scope="col" class="border-0">Rodzaj operacji</th>
            <th scope="col" class="border-0 text-center">Stan przed</th>
            <th scope="col" class="border-0 text-center">Stan po</th>
            <th scope="col" class="border-0">Data zmiany</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(usage, index) of usages"
            :key="'usage-' + index">
            <td class="align-middle">
              {{ usage.material_name }}<br>
              <small>
                Kolor:
                <span class="text-primary">{{ usage.color_name }}</span>
              </small>
              <br v-if="usage.size_name">
              <small v-if="usage.size_name">
                Rozmiar:
                <span class="text-primary">{{ usage.size_name }}</span>
              </small>
            </td>
            <td class="align-middle">
              <span
                v-if="usage.operation_type === 'ORDER'"
                class="text-primary">
                Zamówienie
              </span>
              <span
                v-else-if="usage.operation_type === 'CANCEL_ORDER'"
                class="text-danger">
                Anulowanie zamówienia
              </span>
              <span
                v-else-if="usage.operation_type === 'STOCKTAKING'"
                class="text-success">
                Inwentaryzacja (excel)
              </span>
              <span
                v-else-if="usage.operation_type === 'MANUALLY_UPDATE'"
                class="text-danger">
                Ręczna aktualizacja
             </span>

             <small v-if="usage.operation_type === 'ORDER' || usage.operation_type === 'CANCEL_ORDER'">
              Zamówienie:
              <template v-if="usage.order_name">
                {{ usage.order_name }}
              </template>
              <template v-else>
                &mdash;
              </template>
             </small>
            </td>
            <td class="align-middle text-center">
              {{ usage.stock_bolt_before }}<br>
              <small>
                ({{ usage.stock_cm_before }} cm<sup>2</sup>)
              </small>
            </td>
            <td class="align-middle text-center">
              {{ usage.stock_bolt_after }}<br>
              <small>
                ({{ usage.stock_cm_after }} cm<sup>2</sup>)
              </small>
            </td>
            <td class="align-middle">
              {{ usage.created_at.split('.')[0].split('T').join(' ') }}
            </td>
          </tr>
          <tr v-if="usages.length === 0">
            <td
              colspan="6"
              class="text-center">
              Brak historii do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'materials-stock-usage',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    materialsList () {
      return this.materials.map(material => ({
        text: material.name,
        value: material.id
      }));
    },
    materialColors () {
      let output = [];

      for (let i = 0; i < this.materials.length; i++) {
        let material = this.materials[i];

        output = output.concat(material.colors.map(color => ({
          text: color.name,
          value: color.id
        })));
      }

      return output;
    },
    sizesList () {
      return this.sizes.map(size => ({
        text: size.name,
        value: size.id
      }));
    }
  },
  data() {
    return {
      materials: [],
      sizes: [],
      loaded: false,
      loadError: false,
      loadedMaterials: false,
      loadedSizes: false,
      filters: {
        material_id: null,
        size_id: null,
        color_id: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/materials/items',
        outputKey: 'materials',
        loadedKey: 'loadedMaterials',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/sizes/items',
        outputKey: 'sizes',
        loadedKey: 'loadedSizes',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.material_id) {
        where['material_id'] = this.filters.material_id;
      }

      if (this.filters.color_id) {
        where['color_id'] = this.filters.color_id;
      }

      if (this.filters.size_id) {
        where['size_id'] = this.filters.size_id;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/materials-stock/color-usages/items?page=' + this.currentPage,
        listField: 'usages',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        material_id: null,
        size_id: null,
        color_id: null
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['materials-stock-filters-form'], this.debouncedLoadFilteredData);
    }
  }
};
</script>

